import { useLocation, useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { Price } from 'hb-react/shared/apps/Events/components/Price';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { type CartItem } from 'hb-react/shared/components/ShoppingCard/hooks/v2';

export const ShoppingCardLine = ({ item }: { item: CartItem }) => {
  const { line_itemable_type: type } = item;

  if (type === 'NetworkEvents::Ticket') {
    return <Ticket ticket={item} />;
  }

  if (type === 'Ecommerce::Products::EventAddon') {
    return <Addon addon={item} />;
  }
  if (type === 'Donation::Campaign') {
    return <Donation donation={item} />;
  }
  if (type === 'Ecommerce::PromoCode') {
    return <PromoCode promoCode={item} />;
  }

  return null;
};

const Line = ({ quantity = 1, name, price, currency, mb, ...boxProps }) => (
  <Box justifyContent="space-between" alignItems="center" flexGrow={1} mb={mb}>
    <Box fontSize={1} {...boxProps}>
      {quantity > 1 && <>{quantity} x</>} {name}
    </Box>
    <Box fontSize={1} textAlign="right" pl={1}>
      <Price price={price * (quantity || 1)} currency={currency} />
    </Box>
  </Box>
);

const Ticket = ({ ticket }: { ticket: CartItem }) => (
  <Line
    quantity={ticket.quantity}
    name={ticket.name}
    price={ticket.total}
    currency={ticket.currency}
    fontWeight={1}
    mb={2}
  />
);

const Addon = ({ addon }: { addon: CartItem }) => (
  <Line
    quantity={addon.quantity}
    name={addon.name}
    price={addon.total}
    currency={addon.currency}
    fontWeight={1}
    mb={4}
  />
);

const PromoCodeName = ({ promoCode }) => {
  const { actions, canEditCartItems } = useBooking();

  return (
    <>
      {promoCode.name}
      {canEditCartItems && (
        <Button
          dataTestId={`delete-card-item-${promoCode.id}`}
          icon={() => (
            <FontAwesomeIcon
              height="16px"
              iconName="xmark"
              iconType={CATEGORIES.SOLID}
            />
          )}
          ml={2}
          onClick={() => actions.items.remove(promoCode.id)}
          size="small"
          variant="inversed"
        />
      )}
    </>
  );
};

const PromoCode = ({ promoCode }) => (
  <Line
    alignItems="center"
    currency={promoCode.currency}
    quantity={promoCode.quantity}
    fontWeight={1}
    mb={4}
    name={<PromoCodeName promoCode={promoCode} />}
    price={promoCode.total}
  />
);

const DonationName = ({ donation }: { donation: CartItem }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { canEditCartItems } = useBooking();

  const onEdit = () => navigate(`${location.pathname}/donations`);

  return (
    <>
      {donation.name}
      {canEditCartItems && (
        <Button
          dataTestId={`edit-donation-${donation.line_itemable_id}`}
          icon={() => (
            <FontAwesomeIcon
              height="14px"
              iconName="pen"
              iconType={CATEGORIES.SOLID}
            />
          )}
          ml={2}
          onClick={onEdit}
          size="small"
          variant="inversed"
        />
      )}
    </>
  );
};

const Donation = ({ donation }: { donation: CartItem }) => (
  <Line
    alignItems="center"
    currency={donation.currency}
    fontWeight={1}
    mb={4}
    name={<DonationName donation={donation} />}
    price={donation.total}
  />
);
